import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {DbActiveModal} from "../../../../modules/modal/modal-ref";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {TypeFormAnswers} from "../../../../add-website/typeFormAnswers";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../store";
import {Subject} from "rxjs";
import {combineLatest} from "rxjs";
import {UserSubscription} from "../../../../user/user-subscription";
import {modalSlideAnimation, scaleAnimation} from "../../../utils/animations";
import {UserService} from "../../../../user/user.service";
import {GlobalEvent} from "../../../services/global-event-dispatcher/global-event";
import {AccountUpgradedDialogComponent} from "../account-upgraded-dialog/account-upgraded-dialog.component";
import {DialogService} from "../../../../../ng2-bootstrap-modal/dialog.service";
import {UserManagementService} from "../../../../user-managment/user-management.service";
import {GlobalEventDispatcherService} from "../../../services/global-event-dispatcher/global-event-dispatcher.service";
import {Router} from "@angular/router";
import {User} from "../../../../user/user";
import {CookieService} from "ngx-cookie";
import {Utils} from "../../../utils/utils";
import {Website} from "../../../../websites/website/website";
import {ChosenPlan} from "../../payment-info/models/plan.model";

export type OneMonthFreeMode =
    'creation'
    | 'feature'
    | 'editor'
    | 'ssh'
    | 'optimize'
    | 'wp-admin'
    | 'builder'
    | 'domain'
    | 'general';
export type OneMonthFreeStep = 'features' | 'experts' | 'plans' | 'checkout';

@Component({
    selector: 'db-one-month-free',
    templateUrl: './one-month-free.component.html',
    styleUrls: ['./one-month-free.component.scss'],
    animations: [modalSlideAnimation, scaleAnimation],
    standalone: false
})
export class OneMonthFreeComponent implements OnInit, OnDestroy {

    @Input() mode: OneMonthFreeMode = 'general';
    @Input() isInvited: boolean = false;
    @Input() chosenPlan?: ChosenPlan;
    @Input() couponCode?: string = '1wf';
    @Input() domainName?: string;
    @Input() step: OneMonthFreeStep = 'features';
    @Input() hideBackButton?: boolean;
    @Input() showAnnual?: boolean;
    @Input() mixed?: boolean;
    @Input() fromChat?: boolean;
    @Input() showAsDemo?: boolean = false;
    @Input() hideTab?: boolean = false;
    @Input()  customDomainFlow = false;

    @Output() readonly resized = new EventEmitter<[number, number]>();
    public isAgency: boolean = true;
    public userSubscription: UserSubscription;
    public user: User;
    public demoUser: boolean;
    public expirationDate: string;
    public videoPoster: string;
    public videoSrc: string;
    public videoDuration: number = 16;
    public isMobile: boolean;
    public newPlans: boolean = true;
    public isEcommerceWebsite = false;
    public englishCountries = false;
    public showBackButton = true;
    public userLocalStorage: any;

    private readonly _destroyStream$ = new Subject<boolean>();

    constructor(
        private readonly activeModal: DbActiveModal,
        private readonly _store: Store<fromStore.DbStoreState>,
        private readonly _router: Router,
        private readonly _userService: UserService,
        private readonly _userManagementService: UserManagementService,
        private readonly _globalEventDispatcher: GlobalEventDispatcherService,
        private readonly _dialogService: DialogService,
        private readonly _cookieService: CookieService,
    ) {
    }

    ngOnInit() {
        if (!['builder', 'ssh', 'editor'].includes(this.mode)) {
            this.videoSrc = 'assets/videos/new-1wf-popup.mp4';
            this.videoPoster = 'assets/images/backgrounds/new-1wf-popup-frame.jpeg';
        } else {
            this.videoPoster = 'assets/images/backgrounds/drag-and-drop-frame.jpeg';
            this.videoSrc = 'assets/videos/drag-and-drop-video.mp4';
        }
        this.isMobile = window.innerWidth <= 991;
        this.chosenPlan && this.changePlan(this.chosenPlan);
        this.resize();
        this._getUserType();
    }

    ngOnDestroy() {
        this._destroyStream$.next(true);
        this._destroyStream$.complete();
    }

    public resize(): void {
      if ((this.isEcommerceWebsite && this.mode === 'editor' && this.step === 'features') || this.mode === 'domain') {
        this.activeModal.resize(582, 1180);
      }
      else if (this.step === 'features') {
          this.activeModal.resize(600, ['builder', 'ssh'].includes(this.mode) ? 940 : 1180);
      }
    }

    private _getUserType(): void {
        this._store.select(fromStore.getUserLoaded)
            .pipe(
                first(loaded => loaded),
                switchMap(() => combineLatest(
                    this._store.select(fromStore.getUser),
                    this._store.select(fromStore.getUserSubscription),
                    this._store.select(fromStore.getWebsites),
                  this._store.select(fromStore.getUserLocalStorage)
                )),
                filter(([user, userSubscription]) => !!user && !!userSubscription),
                takeUntil(this._destroyStream$),
            )
            .subscribe(([user, userSubscription, websites, userLocalStorage]) => {
                this.isEcommerceWebsite = websites.some((website: Website) => !!website.isEcommerceWebsite);
                if (this.isEcommerceWebsite && this.mode === 'editor' && this.step === 'features') {
                  this.activeModal.resize(582, 1180);
                }
                this.isAgency = user.signUpFor && user.signUpFor.Role
                    && [TypeFormAnswers.AGENCY].includes(user.signUpFor.Role.id) || false;
                this.userSubscription = userSubscription;
                this.user = user;
                this.userLocalStorage = userLocalStorage;
                this.englishCountries = userLocalStorage && !!userLocalStorage.tier1B;
                this.demoUser = !!this.showAsDemo || Utils.isDemoUser(userSubscription, user);

            });
    }

    public changePlan(plan: ChosenPlan): void {
        this.chosenPlan = plan;
        this.couponCode = plan.coupon;
        const date = new Date();
        date.setDate(date.getDate() + 7);
        this.expirationDate = date.toDateString();
    }

    public showVideo($event): void {
        this.videoPoster = $event.poster;
        this.videoSrc = $event.src;
        this.videoDuration = $event.duration;
    }

    public closeModal(isUpgraded: boolean = false): void {
        if (isUpgraded) {
          if (this.domainName) {
            this._generateDomainInfo(this.domainName);
          }
            this._showSuccessfullyUpgradedDialog();
        }
        this.activeModal.close();
    }

  private _generateDomainInfo(domainName): void {
    const domainInfo = JSON.stringify({
      "want_domain": 1,
      "domain_name": domainName || ""
    });
    localStorage.setItem('dbOnboardingDomainInfo', domainInfo);
    this.userLocalStorage['suggestedDomainName'] = domainName;
    this._updateLocalStorage();
  }

  private _updateLocalStorage(): void {
    this._userService.setToLocal(this.userLocalStorage)
      .pipe(
        first(response => !!response, null),
      )
      .subscribe(response => {
        if (response.success) {
          this._store.dispatch(new fromStore.LoadUserLocalStorage());
          this._store.dispatch(new fromStore.LoadUser());
        }
      });
  }

    private _showSuccessfullyUpgradedDialog(): void {
        this._dialogService.addDialog(AccountUpgradedDialogComponent, {
            currentSubscription: this.userSubscription,
            upgradedFromTrial: true,
            upgradedFromBooster: false,
            isDemoUser: !!Utils.isDemoUser(this.userSubscription, this.user)
        });
    }

    public addOwnWorkspace(event?) {
        if (!this.isInvited) {
            return;
        }

        if (!localStorage.getItem('dbPreviousWorkspaceId') || !localStorage.getItem('dbPreviousWorkspaceRoleId')) {
            localStorage.setItem('dbPreviousWorkspaceId', localStorage.getItem('dbCurrentWorkspaceId'));
            localStorage.setItem('dbPreviousWorkspaceRoleId', localStorage.getItem('dbCurrentWorkspaceRoleId'));
        }
        const switchToWorkspace = {
            ownerWorkspaceId: localStorage.getItem('dbOwnWorkspaceId'),
            currentWorkspaceId: localStorage.getItem('dbOwnWorkspaceId'),
            currentWorkspaceRoleId: 1
        };
        this._store.dispatch(new fromStore.SwitchWorkspace({switching: true, workspaceName: '', redirect: false}));
        this._userManagementService.switchWorkspace(switchToWorkspace.currentWorkspaceId)
            .pipe(
                first(response => !!response, null),
            )
            .subscribe(response => {
                if (response.success) {
                    this._globalEventDispatcher.dispatchEvent(GlobalEvent.workspaceSwitch);
                    this._router.navigate(['/upgrade-plan']);
                    this._store.dispatch(new fromStore.SwitchWorkspace({switching: true, workspaceName: '', redirect: false}));
                }
            });
    }
}
